import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { Tab, TabGroup } from '@sb1/ffe-tabs-react';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import { canUserEditProject, isUserAndReceiverSame } from '../../user/user-selectors';
import { useProject } from '../project-hooks/useProject';
import { useUser } from '../../user/useUser';
import { getUpdatesForProjectId } from '../project-update-reducer';
import { useProjectTimeline } from '../project-timeline/useProjectTimeline';
import Notification from '../../components/notification/Notification';
import I18nText from '../i18n/I18nText';
import { ACTIVITY_TEXT_KEY, DESCRIPTION_TEXT_KEY, UPDATES_TEXT_KEY } from '../i18n/project-i18n-model';
import styles from './project-navigation.module.less';
import { userOwnsProject } from '../project-model';

const ProjectNavigation = ({ disabled = false }) => {
    const project = useProject();
    const canShowHistory = useProjectTimeline(project);
    const user = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const projectUpdates = useSelector((state) => getUpdatesForProjectId(state, project.id));
    const numberOfUpdates = projectUpdates?.length ?? 0;

    const shouldShowUpdates =
        canUserEditProject(user, project.id) || isUserAndReceiverSame(user, project?.receiver) || numberOfUpdates > 0;

    const isDescription = location.pathname === `/project/${project.id}`;
    const isActivity = location.pathname.startsWith(`/project/${project.id}/history`);
    const isUpdates = location.pathname.startsWith(`/project/${project.id}/update`);
    const isProjectOwner = userOwnsProject(project, user?.id);

    return (
        <nav
            className={[styles.navigation, !project.img_url && !isProjectOwner && styles.navigationNoImage].join(' ')}
            aria-label="Navigasjon på spleisen"
        >
            <TabGroup>
                <Tab
                    selected={isDescription}
                    disabled={disabled}
                    onClick={() => {
                        trackEvent('project', 'description-open', { 'project-id': project.id });
                        navigate(`/project/${project.id}`, {
                            preventScrollReset: true,
                            state: { useLocationKey: true },
                        });
                    }}
                    aria-controls="project-description"
                >
                    <I18nText textKey={DESCRIPTION_TEXT_KEY} />
                </Tab>
                {canShowHistory && (
                    <Tab
                        selected={isActivity}
                        disabled={disabled}
                        onClick={() => {
                            trackEvent('project', 'history-open', { 'project-id': project.id });
                            navigate(`/project/${project.id}/history`, {
                                preventScrollReset: true,
                                state: { useLocationKey: true },
                            });
                        }}
                        aria-controls="project-timeline"
                    >
                        {project?.profile?.projectActivityTitle || <I18nText textKey={ACTIVITY_TEXT_KEY} />}
                    </Tab>
                )}
                {shouldShowUpdates && (
                    <Tab
                        selected={isUpdates}
                        disabled={disabled}
                        onClick={() => {
                            trackEvent('project', 'update-open', { 'project-id': project.id });
                            navigate(`/project/${project.id}/update`, {
                                preventScrollReset: true,
                                state: { useLocationKey: true },
                            });
                        }}
                        aria-controls="project-updates"
                    >
                        <I18nText textKey={UPDATES_TEXT_KEY} />
                        <Notification isVisible={numberOfUpdates > 0}>{numberOfUpdates}</Notification>
                    </Tab>
                )}
            </TabGroup>
        </nav>
    );
};

export default ProjectNavigation;
